import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { TicketTyp } from '../types';

export function Card({ title, actions, children, padded = true }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; padded?: boolean }) {
  return (
    <section className="card">
      {(title || actions) && (
        <header>
          {title && <h2>{title}</h2>}
          <span className="spacer" />
          {actions}
        </header>
      )}
      {padded ? <div className="body">{children}</div> : children}
    </section>
  );
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <span className="small muted">{hint}</span>}
    </label>
  );
}

export function Notice({ kind, children }: { kind: 'error' | 'success' | 'info' | 'warn'; children: ReactNode }) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function Badge({ kind = 'default', children }: { kind?: 'default' | 'ok' | 'warn' | 'bad' | 'info'; children: ReactNode }) {
  return <span className={kind === 'default' ? 'badge' : `badge ${kind}`}>{children}</span>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

export function Loading() {
  return <div className="empty">Wird geladen …</div>;
}

export function Dialog({
  titel,
  children,
  aktionen,
  onClose,
  breit,
}: {
  titel: string;
  children: ReactNode;
  aktionen?: ReactNode;
  onClose: () => void;
  breit?: boolean;
}) {
  useEffect(() => {
    const taste = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', taste);
    return () => window.removeEventListener('keydown', taste);
  }, [onClose]);
  return (
    <div className="dialog-hinter" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={breit ? 'dialog breit' : 'dialog'} role="dialog" aria-label={titel}>
        <header>
          <h2>{titel}</h2>
          <span className="spacer" />
          <button className="link" onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </header>
        <div className="body">{children}</div>
        {aktionen && <footer>{aktionen}</footer>}
      </div>
    </div>
  );
}

export const TYP_LABEL: Record<TicketTyp, string> = {
  rueckruf: 'Rückruf',
  material: 'Material bestellen',
  nacharbeit: 'Nacharbeit',
  sonstiges: 'Sonstiges',
};

export const TYP_SYMBOL: Record<TicketTyp, string> = {
  rueckruf: '📞',
  material: '📦',
  nacharbeit: '🔧',
  sonstiges: '📌',
};

export function kundenName(k: { vorname?: string | null; nachname?: string | null; firma?: string | null } | null | undefined): string {
  if (!k) return '';
  const person = [k.vorname, k.nachname].filter(Boolean).join(' ');
  if (person && k.firma) return `${person} (${k.firma})`;
  return person || k.firma || '';
}
